import React, { useState, useEffect, useMemo, useRef } from "react";
import iphone from "../assets/iphone.png";
import ContinueButton from "../components/Buttons/ContinueButton";
import { Button } from "@chakra-ui/react";
import { useSingleToast } from "@/hooks/useSingleToast";
import { Navigate, useNavigate } from "react-router-dom";
import LogoAndText from "@/components/LogoAndText";
import { parrotUserStorage } from "../api/parrotUserStorage";
import { tokenStorage } from "../api/tokenStorage";
import { postTempUser } from "../api/Api";
import { PostTempUserRequest } from "@shared_types";
import { useQuery } from "@tanstack/react-query";
import { GetUserInfoResponse } from "@shared_types";
import { axiosInstance } from "@/api/Api";
import { LoadingPage } from "./LoadingPage";
import { wasJustCreated } from "./RedirectPage";
import { usePostHog } from "posthog-js/react";
import { withRetry } from "@/lib/utils";

const StartPage: React.FC = () => {
  const nonEssentialImages = useMemo(() => [iphone], []);
  const [_nonEssentialImagesLoaded, setNonEssentialImagesLoaded] =
    useState(false);
  const nav = useNavigate();
  const [hasTempUserCreated, setHasTempUserCreated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();
  const once = useRef(false);
  const toast = useSingleToast();

  // Load non-essential images after essential ones are loaded
  useEffect(() => {
    const loadNonEssentialImages = async () => {
      const promises = nonEssentialImages.map((src) => {
        return new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = src;
          img.onload = () => resolve();
          img.onerror = () => reject();
        });
      });

      try {
        await Promise.all(promises);
        setNonEssentialImagesLoaded(true);
      } catch (error) {
        console.error("Error loading non-essential images", error);
        setNonEssentialImagesLoaded(true);
      }
    };

    loadNonEssentialImages();
  }, [nonEssentialImages]);

  const userInfoQuery = useQuery({
    queryKey: ["AuthQueryLoginPage"],
    retry: 0,
    queryFn: async () => {
      const authToken = tokenStorage.getToken();
      const response = await axiosInstance.get<GetUserInfoResponse>(
        "/login/status",
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      return response.data;
    },
  });
  // If the user is logged out we will get id 0, hence we
  // dont want to be redirecting
  let userInfo: GetUserInfoResponse | undefined =
    userInfoQuery.data || undefined;
  if (userInfoQuery.data && userInfoQuery.data.id == 0) {
    userInfo = undefined;
  }
  const recentlyCreated =
    userInfo != undefined && wasJustCreated(userInfo.joinDate, 1800);

  const shouldRedirect =
    userInfo != undefined &&
    !userInfo.isTemp &&
    !userInfo.isTempTrial &&
    !userInfo.isTempPremium &&
    !recentlyCreated;

  const isTrialOrPremium =
    userInfo != undefined && (userInfo.isTrial || userInfo.isPremium);

  const shouldGoToSignup =
    userInfo != undefined && (userInfo.isTempTrial || userInfo.isTempPremium);

  const isPageLoading = tokenStorage.isTokenSet() && userInfoQuery.isPending;

  useEffect(() => {
    once.current = true;
    try {
      posthog.reset();
    } catch (err) {
      console.error("Failed to reset posthog:", err);
    }
  }, []);

  // Only wait for essential images to show the page
  if (isPageLoading) return <LoadingPage />;

  if (shouldGoToSignup) {
    return <Navigate to="/signup" />;
  }

  if (shouldRedirect || isTrialOrPremium) {
    return <Navigate to="/home/learn" />;
  }

  if (
    recentlyCreated &&
    userInfo &&
    !userInfo.isPremium &&
    !userInfo.isTrial &&
    !userInfo.isTemp
  ) {
    return <Navigate to="/onboarding" />;
  }

  const handleTempUserCreation = async () => {
    setIsLoading(true);
    const req: PostTempUserRequest = {
      firstName: "temp",
      email: "temp@temp.com",
    };

    const createTempUserWithRetry = async (
      retries = 8,
      delay = 2500,
      maxDelay = 5000
    ): Promise<void> => {
      try {
        const res = await withRetry(
          () => postTempUser(req),
          retries,
          delay,
          maxDelay
        );
        if (res) {
          tokenStorage.setToken(res.token);
          parrotUserStorage.setId(res.parrotUserId);
          setHasTempUserCreated(true);
          return;
        }
      } catch (err) {
        console.error(
          "Failed to create temp user after multiple attempts",
          err
        );
        toast({
          title: "You appear to be offline",
          description: "Please reconnect your device and try again.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    try {
      await createTempUserWithRetry();
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    if (!hasTempUserCreated) {
      handleTempUserCreation();
      nav("/onboarding");
    } else {
      nav("/onboarding");
    }
  };

  const handleLogin = () => {
    nav("/login");
  };

  return (
    <div className="appPadding flex flex-col h-full w-full overflow-hidden items-center z-0 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 mt-14">
        <LogoAndText />
        <div className="w-72 text-center text-sm mt-4 flex flex-col pb-4">
          <span>Done with Duolingo? </span>
          <span>Start speaking real Español.</span>
        </div>
        <div
          className="inline-block px-3 py-1.5 rounded-full bg-blue-50/70 text-[var(--color-blue)] text-sm"
          style={{
            boxShadow:
              "0 1px 4px -1px rgba(0,122,255,0.12), inset 0 1px 0px 0px rgba(255,255,255,0.6)",
          }}
        >
          <div className="flex flex-row items-center justify-center gap-1.5 text-[var(--color-blue)]">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  className="w-[14px] h-[14px]"
                  viewBox="0 0 24 24"
                  fill={i < 4 || i === 4 ? "#007AFF" : "#D1D1D6"}
                >
                  <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
                </svg>
              ))}
            </div>
            <div className="leading-tight">
              <span className="font-medium">4.9</span>
              <span className="text-[var(--color-blue)]/70 text-sm">
                {" "}
                • App Store Rating
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 flex flex-col justify-end items-center w-full z-10 p-2 pb-4">
        <ContinueButton
          onClick={handleContinue}
          text="Get started"
          disabled={isLoading}
        />
        <Button
          variant="ghost"
          onClick={handleLogin}
          color={"var(--color-grey)"}
          height="var(--button-height)"
          fontSize="base"
          className={`w-full text-center rounded-xl break-words`}
          _hover={{ bg: "transparent" }}
          _active={{ bg: "transparent" }}
        >
          I already have an account
        </Button>
      </div>
    </div>
  );
};

export default StartPage;
